import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '@/lib/supabaseClient';

import {
    closeRefereeProposal,
    fetchPendingProposalMatches,
    fetchPendingProposals,
    fetchProposalAids,
    fetchProposalsForMatches,
    fetchUsualTrios,
    proposeReferee,
} from '@/services/refereeProposalService';
import {
    REFEREE_PROPOSALS_QUERY_KEY_PREFIX,
    previousSeason,
    type RefereeProposal,
    type RefereeProposalAid,
    type UsualTrio,
} from '@/lib/refereeProposals/refereeProposals';

const EMPTY: RefereeProposal[] = [];
const EMPTY_AIDS: Map<string, RefereeProposalAid> = new Map();
const EMPTY_ROLES: RefereeRole[] = [];
const EMPTY_TRIOS: UsualTrio[] = [];

/** Every proposal of these matches. Errors resolve to none (the feature is advisory for the league). */
export function useRefereeProposals(matchIds: string[], enabled = true): { data: RefereeProposal[]; isLoading: boolean } {
    const ids = useMemo(() => [...new Set(matchIds.filter((id) => id && !id.startsWith('temp-')))].sort(), [matchIds]);
    const { data, isLoading } = useQuery({
        queryKey: [REFEREE_PROPOSALS_QUERY_KEY_PREFIX, 'matches', ids],
        queryFn: () => fetchProposalsForMatches(ids),
        enabled: enabled && ids.length > 0,
        staleTime: 30_000,
    });
    return { data: data ?? EMPTY, isLoading };
}

export function usePendingRefereeProposals(leagueIds: string[], season: string | null, enabled = true): RefereeProposal[] {
    const ids = useMemo(() => [...new Set(leagueIds.filter(Boolean))].sort(), [leagueIds]);
    const { data } = useQuery({
        queryKey: [REFEREE_PROPOSALS_QUERY_KEY_PREFIX, 'pending', ids, season],
        queryFn: () => fetchPendingProposals(ids, season ?? ''),
        enabled: enabled && ids.length > 0 && !!season,
        staleTime: 60_000,
    });
    return data ?? EMPTY;
}

/** Pending proposals grouped per match, for the league's notification bell. */
export function usePendingProposalMatches(leagueIds: string[], season: string | null, enabled = true) {
    const ids = useMemo(() => [...new Set(leagueIds.filter(Boolean))].sort(), [leagueIds]);
    return useQuery({
        queryKey: [REFEREE_PROPOSALS_QUERY_KEY_PREFIX, 'pending-matches', ids, season],
        queryFn: () => fetchPendingProposalMatches(ids, season ?? ''),
        enabled: enabled && ids.length > 0 && !!season,
        staleTime: 60_000,
    });
}

/** officialId → aids for the picker of one match. */
export function useRefereeProposalAids(matchId: string | null): Map<string, RefereeProposalAid> {
    const { data } = useQuery({
        queryKey: [REFEREE_PROPOSALS_QUERY_KEY_PREFIX, 'aids', matchId],
        queryFn: async () => new Map((await fetchProposalAids(matchId!)).map((a) => [a.officialId, a])),
        enabled: !!matchId && !matchId.startsWith('temp-'),
        staleTime: 60_000,
    });
    return data ?? EMPTY_AIDS;
}

const useInvalidateProposals = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: [REFEREE_PROPOSALS_QUERY_KEY_PREFIX] });
};

export function useProposeReferee() {
    const invalidate = useInvalidateProposals();
    return useMutation({
        mutationFn: (v: { matchId: string; role: string; officialId: string }) => proposeReferee(v.matchId, v.role, v.officialId),
        onSettled: invalidate,
    });
}

export function useCloseRefereeProposal() {
    const invalidate = useInvalidateProposals();
    return useMutation({
        mutationFn: (v: { proposalId: string; status: 'withdrawn' | 'refused' }) => closeRefereeProposal(v.proposalId, v.status),
        onSettled: invalidate,
    });
}

export interface RefereeRole {
    roleKey: string;
    nameFr: string;
    nameAr: string | null;
    displayOrder: number;
}

/** Active referee slots (role_category 'arbitre') of a league: the only slots he may propose for. */
export function useRefereeRoles(leagueId: string | null): RefereeRole[] {
    const { data } = useQuery({
        queryKey: [REFEREE_PROPOSALS_QUERY_KEY_PREFIX, 'roles', leagueId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('league_official_roles')
                .select('role_key, name_fr, name_ar, display_order')
                .eq('league_id', leagueId!)
                .eq('role_category', 'arbitre')
                .eq('is_active', true)
                .order('display_order', { ascending: true });
            if (error) throw error;
            return (data ?? []).map((r: any) => ({
                roleKey: r.role_key as string,
                nameFr: r.name_fr as string,
                nameAr: (r.name_ar as string | null) ?? null,
                displayOrder: r.display_order as number,
            }));
        },
        enabled: !!leagueId,
        staleTime: 5 * 60_000,
    });
    return data ?? EMPTY_ROLES;
}

/** Usual trios of a league: this season and the previous one (fallback early in a season). */
export function useUsualTrios(leagueId: string | null, season: string | null): { current: UsualTrio[]; previous: UsualTrio[] } {
    const prev = season ? previousSeason(season) : null;
    const current = useQuery({
        queryKey: [REFEREE_PROPOSALS_QUERY_KEY_PREFIX, 'trios', leagueId, season],
        queryFn: () => fetchUsualTrios(leagueId!, season!),
        enabled: !!leagueId && !!season,
        staleTime: 10 * 60_000,
    });
    const previous = useQuery({
        queryKey: [REFEREE_PROPOSALS_QUERY_KEY_PREFIX, 'trios', leagueId, prev],
        queryFn: () => fetchUsualTrios(leagueId!, prev!),
        enabled: !!leagueId && !!prev,
        staleTime: 60 * 60_000,
    });
    return { current: current.data ?? EMPTY_TRIOS, previous: previous.data ?? EMPTY_TRIOS };
}
